import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart
} from "recharts";
import {
  Wine, LogOut, Plus, Upload, CalendarIcon, TrendingUp,
  Users, ShoppingCart, DollarSign, BarChart3, LineChartIcon, PieChartIcon,
  Sun, Moon, Palette, Activity, Award, Zap
} from "lucide-react";
import { cn } from "../lib/utils";
import { useTheme } from "../contexts/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Enhanced color palettes for charts
const CHART_PALETTES = {
  default: {
    primary: "#8B5CF6",
    secondary: "#14B8A6",
    tertiary: "#F59E0B",
    accent: "#EC4899",
  },
  ocean: {
    primary: "#0EA5E9",
    secondary: "#06B6D4",
    tertiary: "#3B82F6",
    accent: "#8B5CF6",
  },
  emerald: {
    primary: "#10B981",
    secondary: "#059669",
    tertiary: "#34D399",
    accent: "#6EE7B7",
  },
  royal: {
    primary: "#8B5CF6",
    secondary: "#A78BFA",
    tertiary: "#C084FC",
    accent: "#E879F9",
  },
  sunset: {
    primary: "#F97316",
    secondary: "#FB923C",
    tertiary: "#FDBA74",
    accent: "#FCA5A5",
  },
  midnight: {
    primary: "#3B82F6",
    secondary: "#60A5FA",
    tertiary: "#93C5FD",
    accent: "#DBEAFE",
  },
};


const Dashboard = ({ user, onLogout }) => {
  const { theme, toggleTheme, colorTheme, setColorTheme } = useTheme();

  const COLORS = CHART_PALETTES[colorTheme] || CHART_PALETTES.default;
  const [summary, setSummary] = useState({ total_sales: 0, mass_traffic_total: 0, walkins_total: 0, record_count: 0 });
  const [dailyData, setDailyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableExpanded, setTableExpanded] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: new Date(2025, 11, 1),
    to: new Date() // Always today
  });
  const [selectedYear, setSelectedYear] = useState(2025);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [chartType, setChartType] = useState("area");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  
  // New sale form with both sections
  const [newSale, setNewSale] = useState({ 
    date: new Date(), 
    massTraffic: "", 
    walkins: "" 
  });
  const [saleDateOpen, setSaleDateOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);

  const token = localStorage.getItem("token") || "";

  const fetchData = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    try {
      const startDate = format(dateRange.from, "yyyy-MM-dd");
      const endDate = format(dateRange.to, "yyyy-MM-dd");

      const [summaryRes, dailyRes, monthlyRes] = await Promise.all([
        axios.get(`${API}/sales/summary`, { headers, params: { start_date: startDate, end_date: endDate } }),
        axios.get(`${API}/sales/daily-aggregated`, { headers, params: { start_date: startDate, end_date: endDate } }),
        axios.get(`${API}/sales/monthly-aggregated`, { headers, params: { year: selectedYear } })
      ]);

      setSummary(summaryRes.data);
      setDailyData(dailyRes.data.map(d => ({
        ...d,
        displayDate: format(
          new Date(d.date + "T12:00:00"),
          "MMM dd"
        ),
        total: d.mass_traffic + d.walkins
      })));
      setMonthlyData(monthlyRes.data.map(d => ({
        ...d,
        displayMonth: `${d.year}-${d.month}`
      })));
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedYear, token, onLogout]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getHeaders = () => ({ Authorization: `Bearer ${token}` });

  const handleAddSale = async (e) => {
    e.preventDefault();
    const dateStr = format(newSale.date, "yyyy-MM-dd");
    const headers = getHeaders();
    
    try {
      const promises = [];
      
      // Add Wines if provided
      if (newSale.massTraffic && parseFloat(newSale.massTraffic) > 0) {
        promises.push(
          axios.post(`${API}/sales`, {
            date: dateStr,
            section: "mass_traffic",
            amount: parseFloat(newSale.massTraffic)
          }, { headers })
        );
      }
      
      // Add walkins if provided
      if (newSale.walkins && parseFloat(newSale.walkins) > 0) {
        promises.push(
          axios.post(`${API}/sales`, {
            date: dateStr,
            section: "walkins",
            amount: parseFloat(newSale.walkins)
          }, { headers })
        );
      }
      
      if (promises.length === 0) {
        toast.error("Please enter at least one sales amount");
        return;
      }
      
      await Promise.all(promises);
      toast.success(`Sales for ${format(newSale.date, "MMM dd, yyyy")} added successfully!`);
      setAddDialogOpen(false);
      setNewSale({ date: new Date(), massTraffic: "", walkins: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add sale");
    }
  };

  const handleCsvUpload = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      toast.error("Please select a CSV file");
      return;
    }

    const formData = new FormData();
    formData.append("file", csvFile);

    try {
      const response = await axios.post(`${API}/sales/upload-csv`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" }
      });
      toast.success(`${response.data.created} records uploaded successfully!`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      setUploadDialogOpen(false);
      setCsvFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    }
  };

  const handleDateSelect = (range) => {
    if (range?.from) {
      setDateRange({
        from: range.from,
        to: range.to || range.from
      });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

const filteredDaily = selectedMonth === "all"
  ? dailyData
  : dailyData.filter(d => new Date(d.date + "T12:00:00").getMonth() + 1 === parseInt(selectedMonth));

const filteredMass = filteredDaily.reduce((a, b) => a + b.mass_traffic, 0);
const filteredWalkins = filteredDaily.reduce((a, b) => a + b.walkins, 0);
const cumulativeData = dailyData.reduce((acc, curr, index) => {
  const prevTotal = index === 0 ? 0 : acc[index - 1].cumulative;
  acc.push({
    ...curr,
    cumulative: prevTotal + curr.total
  });
  return acc;
}, []);

const tableTotals = dailyData.reduce(
  (acc, curr) => {
    acc.mass += curr.mass_traffic;
    acc.walkins += curr.walkins;
    acc.total += curr.total;
    return acc;
  },
  { mass: 0, walkins: 0, total: 0 }
);
  
const pieData = [
  { name: "Wines", value: filteredMass, color: COLORS.primary },
  { name: "Walk-ins", value: filteredWalkins, color: COLORS.secondary }
];

  // New computed data for enhanced visualizations
  const growthRate = monthlyData.length >= 2
    ? ((monthlyData[monthlyData.length - 1]?.total - monthlyData[monthlyData.length - 2]?.total) / monthlyData[monthlyData.length - 2]?.total * 100) || 0
    : 0;

  const topDays = [...dailyData]
    .sort((a, b) => b.total - a.total)
    .slice(0, 5)
    .map((d, i) => ({ ...d, rank: i + 1 }));

  const dayOfWeekData = dailyData.reduce((acc, curr) => {
    const dayOfWeek = new Date(curr.date + "T12:00:00").toLocaleDateString('en-US', { weekday: 'short' });
    if (!acc[dayOfWeek]) acc[dayOfWeek] = { day: dayOfWeek, total: 0, count: 0 };
    acc[dayOfWeek].total += curr.total;
    acc[dayOfWeek].count += 1;
    return acc;
  }, {});

  const dayOfWeekAvg = Object.values(dayOfWeekData).map(d => ({
    day: d.day,
    average: d.total / d.count
  })).sort((a, b) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days.indexOf(a.day) - days.indexOf(b.day);
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card/95 backdrop-blur-md rounded-xl p-4 border border-border shadow-xl">
          <p className="font-heading font-semibold text-foreground mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="font-data text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-300">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})` }}>
              <Wine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl md:text-2xl font-bold tracking-tight" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }} data-testid="dashboard-title">
                Alcohol Sales Ledger
              </h1>
              <p className="text-xs text-muted-foreground hidden sm:block">Alcohol Sales Analytics Dashboard</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-xl hover:bg-accent"
              data-testid="theme-toggle"
            >
              {theme === "dark" ? (
                <Sun className="w-5 h-5 text-amber-400" />
              ) : (
                <Moon className="w-5 h-5 text-slate-700" />
              )}
            </Button>

            <div className="text-right hidden md:block">
              <p className="text-sm font-semibold">{user.username || user.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onLogout}
              className="hover:bg-destructive/20 hover:text-destructive rounded-xl"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 md:py-10 space-y-8">
        {/* Filters Bar */}
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Range Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="glass-subtle h-11 px-4 rounded-xl hover:border-primary/50" data-testid="date-range-picker">
                  <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">
                    {format(dateRange.from, "MMM dd")} - {format(dateRange.to, "MMM dd, yyyy")}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-card border-border rounded-xl shadow-2xl" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange.from}
                  selected={dateRange}
                  onSelect={handleDateSelect}
                  numberOfMonths={2}
                  data-testid="date-calendar"
                />
              </PopoverContent>
            </Popover>


            {/* Chart Type Toggle */}
            <div className="flex items-center glass-subtle rounded-xl p-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setChartType("area")}
                className={cn("h-9 px-3 rounded-lg", chartType === "area" && "bg-primary text-white")}
                data-testid="chart-type-area"
              >
                <LineChartIcon className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setChartType("bar")}
                className={cn("h-9 px-3 rounded-lg", chartType === "bar" && "bg-primary text-white")}
                data-testid="chart-type-bar"
              >
                <BarChart3 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Admin Actions */}
          {user.role === "admin" && (
            <div className="flex items-center gap-2">
              <Select value={colorTheme} onValueChange={setColorTheme}>
                <SelectTrigger className="w-[160px] h-11 glass-subtle">
                  <Palette className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Color Theme" />
                </SelectTrigger>
              
                <SelectContent>
                  <SelectItem value="default">Default Purple</SelectItem>
                  <SelectItem value="ocean">Ocean Blue</SelectItem>
                  <SelectItem value="emerald">Emerald Green</SelectItem>
                  <SelectItem value="royal">Royal Purple</SelectItem>
                  <SelectItem value="sunset">Sunset Orange</SelectItem>
                  <SelectItem value="midnight">Midnight Blue</SelectItem>
                </SelectContent>
              </Select>
              <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="text-white h-11 px-5 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-shadow" style={{ background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.secondary})` }} data-testid="add-sale-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Sales
                  </Button>
                </DialogTrigger>
                <DialogContent className="glass border-border rounded-2xl sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl font-bold" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Add Daily Sales</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddSale} className="space-y-5 mt-4">
                    {/* Date Picker with Calendar */}
                    <div className="space-y-2">
                      <Label className="font-medium">Select Date</Label>
                      <Popover open={saleDateOpen} onOpenChange={setSaleDateOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full glass-subtle h-12 rounded-xl justify-start text-left font-normal"
                            data-testid="add-sale-date-picker"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
                            {format(newSale.date, "EEEE, MMMM dd, yyyy")}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-card border-border rounded-xl" align="start">
                          <Calendar
                            mode="single"
                            selected={newSale.date}
                            onSelect={(date) => {
                              if (date) {
                                setNewSale({ ...newSale, date });
                                setSaleDateOpen(false);
                              }
                            }}
                            initialFocus
                            data-testid="add-sale-calendar"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Wines Input */}
                    <div className="space-y-2">
                      <Label className="font-medium flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.primary }} />
                        Wines Sales (₹)
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Enter Wines amount"
                        value={newSale.massTraffic}
                        onChange={(e) => setNewSale({ ...newSale, massTraffic: e.target.value })}
                        className="glass-subtle h-12 rounded-xl font-data text-lg"
                        data-testid="add-sale-mass-traffic"
                      />
                    </div>

                    {/* Walk-ins Input */}
                    <div className="space-y-2">
                      <Label className="font-medium flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.secondary }} />
                        Walk-ins Sales (₹)
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Enter walk-ins amount"
                        value={newSale.walkins}
                        onChange={(e) => setNewSale({ ...newSale, walkins: e.target.value })}
                        className="glass-subtle h-12 rounded-xl font-data text-lg"
                        data-testid="add-sale-walkins"
                      />
                    </div>

                    {/* Total Preview */}
                    {(newSale.massTraffic || newSale.walkins) && (
                      <div className="p-4 rounded-xl border border-primary/20" style={{ background: `linear-gradient(to right, ${COLORS.primary}10, ${COLORS.secondary}10)` }}>
                        <p className="text-sm text-muted-foreground mb-1">Total for this day</p>
                        <p className="text-2xl font-heading font-bold" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                          {formatCurrency(
                            (parseFloat(newSale.massTraffic) || 0) + (parseFloat(newSale.walkins) || 0)
                          )}
                        </p>
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full text-white h-12 rounded-xl font-semibold"
                      style={{ background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.secondary})` }}
                      data-testid="add-sale-submit"
                    >
                      Add Sales
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="glass-subtle h-11 px-5 rounded-xl font-semibold" data-testid="upload-csv-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload CSV
                  </Button>
                </DialogTrigger>
                <DialogContent className="glass border-border rounded-2xl sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl font-bold" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Upload CSV</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCsvUpload} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="font-medium">CSV File</Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Format: date, section, amount<br/>
                        Example: 2025-12-01, mass_traffic, 15000
                      </p>
                      <Input
                        type="file"
                        accept=".csv"
                        onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                        className="glass-subtle rounded-xl"
                        data-testid="csv-file-input"
                      />
                    </div>
                    <Button type="submit" className="w-full text-white h-12 rounded-xl font-semibold" style={{ background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.secondary})` }} data-testid="csv-upload-submit">
                      Upload
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Total Sales - Purple */}
          <Card className="glass-subtle rounded-2xl overflow-hidden animate-fade-in-up" data-testid="total-sales-card" style={{ boxShadow: `0 0 40px -10px ${COLORS.primary}40` }}>
            <div className="h-1" style={{ background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.primary}dd)` }} />
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Sales</CardTitle>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS.primary}20` }}>
                <DollarSign className="h-5 w-5" style={{ color: COLORS.primary }} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-heading font-bold" style={{ color: COLORS.primary }}>{formatCurrency(summary.total_sales)}</div>
              <p className="text-xs text-muted-foreground mt-1 font-data">{summary.record_count} records</p>
            </CardContent>
          </Card>

          {/* Wines - Teal */}
          <Card className="glass-subtle rounded-2xl overflow-hidden animate-fade-in-up animate-delay-100" data-testid="mass-traffic-card" style={{ boxShadow: `0 0 40px -10px ${COLORS.secondary}40` }}>
            <div className="h-1" style={{ background: `linear-gradient(to right, ${COLORS.secondary}, ${COLORS.secondary}dd)` }} />
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Wines</CardTitle>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS.secondary}20` }}>
                <ShoppingCart className="h-5 w-5" style={{ color: COLORS.secondary }} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-heading font-bold" style={{ color: COLORS.secondary }}>{formatCurrency(summary.mass_traffic_total)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {summary.total_sales > 0 ? ((summary.mass_traffic_total / summary.total_sales) * 100).toFixed(1) : 0}% of total
              </p>
            </CardContent>
          </Card>

          {/* Walk-ins - Amber */}
          <Card className="glass-subtle rounded-2xl overflow-hidden animate-fade-in-up animate-delay-200" data-testid="walkins-card" style={{ boxShadow: `0 0 40px -10px ${COLORS.tertiary}40` }}>
            <div className="h-1" style={{ background: `linear-gradient(to right, ${COLORS.tertiary}, ${COLORS.tertiary}dd)` }} />
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Walk-ins</CardTitle>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS.tertiary}20` }}>
                <Users className="h-5 w-5" style={{ color: COLORS.tertiary }} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-heading font-bold" style={{ color: COLORS.tertiary }}>{formatCurrency(summary.walkins_total)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {summary.total_sales > 0 ? ((summary.walkins_total / summary.total_sales) * 100).toFixed(1) : 0}% of total
              </p>
            </CardContent>
          </Card>

          {/* Avg Daily - Pink */}
          <Card className="glass-subtle rounded-2xl overflow-hidden animate-fade-in-up animate-delay-300" data-testid="trend-card" style={{ boxShadow: `0 0 40px -10px ${COLORS.accent}40` }}>
            <div className="h-1" style={{ background: `linear-gradient(to right, ${COLORS.accent}, ${COLORS.accent}dd)` }} />
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Avg Daily</CardTitle>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS.accent}20` }}>
                <TrendingUp className="h-5 w-5" style={{ color: COLORS.accent }} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-heading font-bold" style={{ color: COLORS.accent }}>
                {formatCurrency(dailyData.length > 0 ? summary.total_sales / dailyData.length : 0)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{dailyData.length} days</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Chart - Daily Sales */}
          <Card className="glass-subtle rounded-2xl lg:col-span-3 animate-fade-in-up" data-testid="daily-sales-chart">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-bold">Daily Sales Trend</CardTitle>
            </CardHeader>
            <CardContent className="chart-container">
              {loading ? (
                <div className="h-[300px] skeleton rounded-xl" />
              ) : dailyData.length === 0 ? (
                <div className="empty-state h-[300px]">
                  <PieChartIcon className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No data available for selected period</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={380}>
                  {chartType === "area" ? (
                    <AreaChart data={dailyData}>
                      <defs>
                        <linearGradient id="colorMassTraffic" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorWalkins" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.secondary} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                      <XAxis dataKey="displayDate" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend 
                        wrapperStyle={{ 
                          color: theme === "dark" ? "#E5E7EB" : "#1F2937" 
                        }}
                        formatter={(value, entry) => (
                          <span style={{ color: entry.color }}>{value}</span>
                        )}
                      />
                      <Area type="monotone" dataKey="mass_traffic" name="Wines" stroke={COLORS.primary} strokeWidth={2} fill="url(#colorMassTraffic)" />
                      <Area type="monotone" dataKey="walkins" name="Walk-ins" stroke={COLORS.secondary} strokeWidth={2} fill="url(#colorWalkins)" />
                      <Area type="monotone" dataKey="total" name="Total Sales" stroke={COLORS.tertiary} strokeWidth={3} fillOpacity={0} />
                    </AreaChart>
                  ) : (
                    <BarChart data={dailyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                      <XAxis dataKey="displayDate" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend 
                        wrapperStyle={{ 
                          color: theme === "dark" ? "#E5E7EB" : "#1F2937" 
                        }}
                        formatter={(value, entry) => (
                          <span style={{ color: entry.color }}>{value}</span>
                        )}
                      />
                      <Bar dataKey="mass_traffic" name="Wines" fill={COLORS.primary} radius={[6, 6, 0, 0]} />
                      <Bar dataKey="walkins" name="Walk-ins" fill={COLORS.secondary} radius={[6, 6, 0, 0]} />
                      <Bar dataKey="total" name="Total Sales" fill={COLORS.tertiary} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          
          <Card className="glass-subtle rounded-2xl lg:col-span-2">
            <CardHeader>
            <CardTitle>Cumulative Revenue Growth</CardTitle>
            </CardHeader>
            
            <CardContent>
            <ResponsiveContainer width="100%" height={250}>
            <LineChart data={cumulativeData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
            <XAxis dataKey="displayDate" stroke="hsl(var(--muted-foreground))" fontSize={11}/>
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`}/>
            <Tooltip content={<CustomTooltip/>}/>
            <Line 
             type="monotone"
             dataKey="cumulative"
             stroke={COLORS.primary}
             strokeWidth={3}
             dot={false}
            />
            </LineChart>
            </ResponsiveContainer>
            </CardContent>
            </Card>
          
          {/* Growth Rate Card - NEW */}
          <Card className="glass-subtle rounded-2xl">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Monthly Growth</CardTitle>
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                <TrendingUp className={cn("h-5 w-5", growthRate >= 0 ? "text-primary" : "text-destructive")} />
              </div>
            </CardHeader>
            <CardContent>
              <div className={cn("text-3xl font-heading font-bold", growthRate >= 0 ? "text-primary" : "text-destructive")}>
                {growthRate >= 0 ? "+" : ""}{growthRate.toFixed(1)}%
              </div>
              <p className="text-xs text-muted-foreground mt-1">vs. previous month</p>
            </CardContent>
            </Card>
          
          {/* Day of Week Average Chart - NEW */}
          <Card className="glass-subtle rounded-2xl lg:col-span-2">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-bold">Average Sales by Day of Week</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dayOfWeekAvg}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="average" name="Avg Sales" fill={COLORS.accent} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Top Performing Days - NEW */}
          <Card className="glass-subtle rounded-2xl">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-bold flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                Top 5 Days
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {topDays.map((day, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm",
                        idx === 0 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                      )}>
                        {day.rank}
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{day.displayDate}</div>
                        <div className="text-xs text-muted-foreground">{formatCurrency(day.total)}</div>
                      </div>
                    </div>
                    <Zap className={cn("w-4 h-4", idx === 0 ? "text-primary" : "text-muted-foreground")} />
                  </div>
                ))}
              </div>
            </CardContent>
            </Card>
          
          {/* Pie Chart - Distribution */}
          <Card className="glass-subtle rounded-2xl animate-fade-in-up animate-delay-100" data-testid="distribution-chart">
            <CardHeader>
              <div className="flex flex-col gap-2">
            
                <CardTitle className="font-heading text-lg font-bold">
                  Sales Distribution
                </CardTitle>
            
                <div className="flex gap-2">
            
                  {/* Year Selector */}
                  <Select
                    value={selectedYear.toString()}
                    onValueChange={(v) => setSelectedYear(parseInt(v))}
                  >
                    <SelectTrigger className="w-[110px] h-9">
                      <SelectValue />
                    </SelectTrigger>
            
                    <SelectContent>
                      {[2025, 2026, 2027].map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
            
                  {/* Month Selector */}
                  <Select
                    value={selectedMonth}
                    onValueChange={setSelectedMonth}
                  >
                    <SelectTrigger className="w-[110px] h-9">
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
            
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
            
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {format(new Date(2025, i, 1), "MMM")}
                        </SelectItem>
                      ))}
            
                    </SelectContent>
                  </Select>
            
                </div>
              </div>
            </CardHeader>
            <CardContent className="chart-container">
              {loading ? (
                <div className="h-[300px] skeleton rounded-xl" />
              ) : summary.total_sales === 0 ? (
                <div className="empty-state h-[300px]">
                  <PieChartIcon className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No data available</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={110}
                      paddingAngle={4}
                      dataKey="value"
                      strokeWidth={0}
                      label={({ value, entry }) => {
                        const total = pieData.reduce((sum, item) => sum + item.value, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${percentage}%`;
                      }}
                      labelLine={false}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value) => <span className="text-foreground text-sm">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Monthly Comparison Chart */}
        <Card className="glass-subtle rounded-2xl animate-fade-in-up" data-testid="monthly-chart">
          <CardHeader>
            <div className="flex items-center justify-between">
<CardTitle className="font-heading text-lg font-bold">Monthly Comparison</CardTitle>

<Select value={selectedYear.toString()} onValueChange={(v)=>setSelectedYear(parseInt(v))}>
  <SelectTrigger className="w-[120px] h-9">
    <SelectValue />
  </SelectTrigger>
  <SelectContent>
    {[2025,2026,2027].map(y=>(
      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
    ))}
  </SelectContent>
</Select>
</div>
          </CardHeader>
          <CardContent className="chart-container">
            {loading ? (
              <div className="h-[300px] skeleton rounded-xl" />
            ) : monthlyData.length === 0 ? (
              <div className="empty-state h-[300px]">
                <BarChart3 className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No monthly data available for {selectedYear}</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis dataKey="displayMonth" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    wrapperStyle={{ 
                      color: theme === "dark" ? "#E5E7EB" : "#1F2937" 
                    }}
                    formatter={(value, entry) => (
                      <span style={{ color: entry.color }}>{value}</span>
                    )}
                  />
                  <Bar dataKey="mass_traffic" name="Wines" fill={COLORS.primary} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="walkins" name="Walk-ins" fill={COLORS.secondary} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="glass-subtle rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Daily Sales Table</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTableExpanded(!tableExpanded)}
            className="rounded-lg"
          >
            {tableExpanded ? "Collapse" : "Expand"}
          </Button>
          </CardHeader>
          
          <CardContent className={`overflow-x-auto ${!tableExpanded ? "max-h-[300px] overflow-y-auto" : ""}`}>
          <table className="w-full border-collapse rounded-xl overflow-hidden">
          
            <thead className="bg-muted">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold whitespace-nowrap">Date</th>
                <th className="px-3 py-2 text-right text-xs font-semibold whitespace-nowrap">Wines</th>
                <th className="px-3 py-2 text-right text-xs font-semibold whitespace-nowrap">Walk-ins</th>
                <th className="px-3 py-2 text-right text-xs font-semibold whitespace-nowrap">Total</th>
              </tr>
            </thead>
          
            <tbody>
              <tr className="bg-muted font-bold border-b">
                <td className="px-3 py-2 text-xs">Total</td>
              
                <td className="px-3 py-2 text-right text-xs" style={{ color: COLORS.primary }}>
                  {formatCurrency(tableTotals.mass)}
                </td>
              
                <td className="px-3 py-2 text-right text-xs" style={{ color: COLORS.secondary }}>
                  {formatCurrency(tableTotals.walkins)}
                </td>
              
                <td className="px-3 py-2 text-right text-xs" style={{ color: COLORS.tertiary }}>
                  {formatCurrency(tableTotals.total)}
                </td>
              </tr>
              
              {dailyData.map((d, i) => (
                <tr
                  key={i}
                  className="border-b hover:bg-muted/50 transition"
                >
                  <td className="px-3 py-2 font-medium text-xs">
                    {d.displayDate}
                  </td>
          
                  <td className="px-3 py-2 text-right font-semibold text-xs" style={{ color: COLORS.primary }}>
                    {formatCurrency(d.mass_traffic)}
                  </td>
          
                  <td className="px-3 py-2 text-right font-semibold text-xs" style={{ color: COLORS.secondary }}>
                    {formatCurrency(d.walkins)}
                  </td>
          
                  <td className="px-3 py-2 text-right font-bold text-xs" style={{ color: COLORS.tertiary }}>
                    {formatCurrency(d.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          
          </table>
          </CardContent>
          </Card>
        
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 py-6 mt-12">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            Alcohol Sales Ledger &copy; {new Date().getFullYear()} - Premium Retail Analytics
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
